import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  IconButton,
  LinearProgress,
  Paper,
  Divider,
  Alert
} from '@mui/material';
import { UploadFile, Description, Delete, Close, CheckCircle, ErrorOutline } from '@mui/icons-material';

const ImportResumes = ({ open, onClose, postID, onImportSuccess }) => {
  const { t } = useTranslation();
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState({});
  const [isDragging, setIsDragging] = useState(false);
  const [uploadError, setUploadError] = useState(null);
  const [uploadResult, setUploadResult] = useState(null);
  const apiUrl = process.env.REACT_APP_API_URL || '';
  
  // Constants based on backend restrictions
  const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
  const MAX_FILES = 1001;
  const ALLOWED_EXTENSIONS = ['pdf']; // Backend only accepts PDF
  
  const handleFileChange = (event) => {
    const newFiles = Array.from(event.target.files);
    addFiles(newFiles);
  };

  const validateFile = (file) => {
    // Check file extension
    const extension = getFileExtension(file.name).toLowerCase();
    if (!ALLOWED_EXTENSIONS.includes(extension)) {
      return t('importResumes.notPdfFile', { fileName: file.name });
    }
    
    // Check file size
    if (file.size > MAX_FILE_SIZE) {
      return t('importResumes.fileTooLarge', { fileName: file.name });
    }
    
    return null; // No errors
  };

  const simulateFileUpload = (file) => {
    setUploading(true);
    setUploadProgress(prev => ({
      ...prev,
      [file.name]: 0
    }));

    const interval = setInterval(() => {
      setUploadProgress(prev => {
        const currentProgress = prev[file.name] || 0;
        const newProgress = currentProgress + 10;

        if (newProgress >= 100) {
          clearInterval(interval);
          setUploading(false);
          return {
            ...prev,
            [file.name]: 100
          };
        }

        return {
          ...prev,
          [file.name]: newProgress
        };
      });
    }, 300);
  };

  const removeFile = (fileName) => {
    setFiles(prev => prev.filter(file => file.name !== fileName));
    setUploadProgress(prev => {
      const newProgress = { ...prev };
      delete newProgress[fileName];
      return newProgress;
    });
  };
  
  const handleDragEnter = (e) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);
    addFiles(droppedFiles);
  };

  // Unified function to handle both input and drop files
  const addFiles = (newFiles) => {
    if (files.length + newFiles.length > MAX_FILES) {
      setUploadError(t('importResumes.maxFilesExceeded', { max: MAX_FILES }));
      return;
    }
    
    const validFiles = [];
    const errors = [];
    
    newFiles.forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(error);
      } else {
        validFiles.push(file);
      }
    });
    
    if (errors.length > 0) {
      setUploadError(t('importResumes.someFilesNotAdded', { errors: errors.join(', ') }));
    }
    
    if (validFiles.length > 0) {
      setFiles(prev => [...prev, ...validFiles]);
      validFiles.forEach(file => simulateFileUpload(file));
    }
  };

  const getFileExtension = (filename) => {
    return filename.slice((filename.lastIndexOf(".") - 1 >>> 0) + 2);
  };

  const isResume = (filename) => {
    const ext = getFileExtension(filename).toLowerCase();
    return ALLOWED_EXTENSIONS.includes(ext);
  };

  // async function Load_applications() {
  //   try {
  //     const response = await fetch(
  //       `${apiUrl}authentication/candidates-scraped-for-post/${postID}/`,
  //       {
  //         headers: {
  //           "Content-type": "application/json",
  //           Authorization: "Bearer " + localStorage.getItem("accessToken"),
  //         },
  //       }
  //     );
  //     if (response.status === 401) {
  //       return;
  //     }
  //     const json = await response.json();
  //     setApplications(json);
  //   } catch (error) {
  //     console.error("Error loading applications:", error);
  //   }
  // };
  const uploadFiles = async () => {
    if (files.length === 0) return;
    
    // Close the dialog immediately when import starts
    onClose();
    
    // Notify parent that import is starting
    if (onImportSuccess) {
      onImportSuccess(files.length); // Pass the number of files
    }
    
    setUploading(true);
    setUploadError(null);
    setUploadResult(null);

    try {
      const formData = new FormData();
      
      // Append each file to the FormData object
      files.forEach((file) => {
        formData.append("files[]", file);
      });
      console.log("Files to upload:", files);
      console.log("FormData:", formData);
      
      // Make the API call
      const response = await fetch(
        `${apiUrl}candidates/uploading_candidates_post/${postID}/`,  //ai
        {
          method: "POST",
          headers: {
            Authorization: "Bearer " + localStorage.getItem("accessToken"),
          },
          body: formData,
        }
      );

      // Parse response data
      const data = await response.json();
      
      // Check if response is successful
      if (!response.ok) {
        throw new Error(data.error || `Upload failed with status: ${response.status}`);
      }

      console.log("Upload response:", data);
      
      // Set upload result
      setUploadResult(data);
      
    } catch (error) {
      console.error("Error uploading files:", error);
      setUploadError(error.message || "Failed to upload files");
    } finally {
      setUploading(false);
      // Clean up state after upload
      setFiles([]);
      setUploadProgress({});
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        elevation: 3,
        sx: { borderRadius: 2 }
      }}
    >
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Typography variant="h6" fontFamily="Poppins" fontWeight={500}>
          {t('importResumes.title')}
        </Typography>
        <IconButton onClick={onClose} size="small">
          <Close />
        </IconButton>
      </DialogTitle>

      <Divider />

      <DialogContent>
        {uploadResult && (
          <Alert 
            severity={uploadResult.success_count > 0 ? "success" : "warning"}
            sx={{ mb: 2 }}
          >
            {uploadResult.success_count} {t('importResumes.filesUploaded')}
            {uploadResult.error_count > 0 && ` ${uploadResult.error_count} ${t('importResumes.filesHadErrors')}`}
          </Alert>
        )}
        
        {uploadError && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {uploadError}
          </Alert>
        )}
        
        <Box sx={{
          mb: 3,
          width: 'calc(100% - 32px)', // Account for dialog padding
          margin: '16px auto',        // Center the box with vertical spacing
        }}>
          <Paper
            variant="outlined"
            sx={{
              width: '100%',          // Take full width of parent container
              padding: 2,
              textAlign: 'center',
              border: `2px dashed ${isDragging ? 'primary.main' : '#ccc'}`,
              borderRadius: 2,
              cursor: 'pointer',
              backgroundColor: isDragging ? 'action.hover' : 'background.paper',
              transition: 'all 0.3s ease',
              '&:hover': {
                borderColor: 'primary.main',
                bgcolor: 'rgba(0, 0, 0, 0.01)'
              }
            }}
            component="label"
            onDragEnter={handleDragEnter}
            onDragOver={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <input
              type="file"
              hidden
              multiple
              accept=".pdf"
              onChange={handleFileChange}
            />
            <UploadFile sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="body1" fontFamily="Poppins" gutterBottom>
              {t('importResumes.dragDrop')}
            </Typography>
            <Typography variant="body2" color="textSecondary" fontFamily="Poppins">
              {t('importResumes.supportedFormat')}
            </Typography>
          </Paper>
        </Box>

        {files.length > 0 && (
          <Box>
            <Typography variant="subtitle1" fontFamily="Poppins" fontWeight={500} sx={{ mb: 1 }}>
              {t('importResumes.uploadedResumes')} ({files.length})
            </Typography>
            <List>
              {files.map((file, index) => (
                <ListItem
                  key={`${file.name}-${index}`}
                  sx={{
                    mb: 1,
                    border: '1px solid #eee',
                    borderRadius: 1,
                    bgcolor: isResume(file.name) ? 'white' : 'rgba(255, 235, 235, 0.3)'
                  }}
                >
                  <ListItemIcon>
                    <Description color={isResume(file.name) ? 'primary' : 'error'} />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography variant="body2" fontFamily="Poppins" fontWeight={500} noWrap>
                        {file.name}
                      </Typography>
                    }
                    secondary={
                      <Typography variant="caption" color="textSecondary">
                        {(file.size / 1024).toFixed(1)} KB
                      </Typography>
                    }
                  />
                  {uploadProgress[file.name] < 100 ? (
                    <Box sx={{ width: '50%', mr: 2 }}>
                      <LinearProgress
                        variant="determinate"
                        value={uploadProgress[file.name] || 0}
                        sx={{ height: 8, borderRadius: 1 }}
                      />
                      <Typography variant="caption" color="textSecondary" align="right" display="block">
                        {uploadProgress[file.name] || 0}%
                      </Typography>
                    </Box>
                  ) : (
                    <ListItemIcon>
                      <CheckCircle color="success" fontSize="small" />
                    </ListItemIcon>
                  )}
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      onClick={() => removeFile(file.name)}
                      size="small"
                    >
                      <Delete fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
          </Box>
        )}

        <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mt: 2 }}>
          {t('importResumes.processingNote')}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <Button
          onClick={onClose}
          color="inherit"
          sx={{ fontFamily: 'Poppins' }}
        >
          {t('importResumes.cancel')}
        </Button>
        <Button
          variant="contained"
          disabled={files.length === 0 || uploading}
          sx={{ fontFamily: 'Poppins' }}
          onClick={uploadFiles}
        >
          {uploading ? t('importResumes.uploading') : `${t('importResumes.import')} ${files.length > 0 ? `(${files.length})` : ''}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ImportResumes;